import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { getProjectOverview, getProjectOverviewSystems } from '../../core/store/selectors/project-overview.selectors';
import { FormControl } from '@angular/forms';
import { ProjectOverview } from '../../shared/models/project-overview';
import { ProjectOverviewSystemState } from '../../core/store/reducers/project-overview';
import { ExcelService } from '@completion/services';

interface OverviewGroup {
  system: string;
  items: ProjectOverview[];
  expanded: boolean;
  status?: ProjectOverviewSystemState['status'];
  errorMessage?: string | null;
  punch: {
    aEntered: number;
    aAccepted: number;
    aCleared: number;
    bEntered: number;
    bAccepted: number;
    bCleared: number;
    total: number;
  };
  certificates: {
    mcc: { issued: number; signed: number };
    rfcc: { issued: number; signed: number };
    rfoc: { issued: number; signed: number };
    none: { count: number; total: number };
  };
  checksheetsSigned: {
    mcc: number;
    rfcc: number;
    rfoc: number;
  };
}

enum OverviewFilter {
  All = 'all',
  SignedMCC = 'signed_mcc',
  SignedRFCC = 'signed_rfcc',
  SignedRFOC = 'signed_rfoc',
  OpenAPunch = 'open_a',
  OpenBPunch = 'open_b',
  RFOCSignedWithOpenPunch = 'rfoc_signed_open_punch',
  SignedChecksheets = 'signed_checksheets',
  VerifiedChecksheets = 'verified_checksheets',
  SignedNotVerified = 'signed_not_verified',
  Errors = 'errors',
}

@Component({
  selector: 'app-project-overview-view',
  standalone: false,
  templateUrl: './project-overview-view.component.html',
  styleUrls: ['./project-overview-view.component.scss'],
})
export class ProjectOverviewViewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  groups$: Observable<OverviewGroup[]>;
  private expandedMap = new Map<string, boolean>();
  private refresh$ = new BehaviorSubject<number>(0);
  search = new FormControl('');
  filterControl = new FormControl<OverviewFilter>(OverviewFilter.All, { nonNullable: true });
  highlightedId: number | null = null;
  private latestGroups: OverviewGroup[] = [];
  private highlightedIds = new Set<number>();
  private filter$ = new BehaviorSubject<OverviewFilter>(OverviewFilter.All);
  activeFilter = OverviewFilter; // expose enum to template
  private filteredScopeOfWork = (items: ProjectOverview[], filter: OverviewFilter) =>
    items.filter((it) => this.itemMatchesFilter(it, filter));
  // Cache for per-checksheet tint styles so we don’t recompute on every change detection
  private checksheetTintCache = new Map<string, { [key: string]: string }>();

  constructor(
    private store: Store,
    private excelService: ExcelService,
  ) {}

  ngOnInit(): void {
    const overview$ = this.store.select(getProjectOverview);
    const systems$ = this.store.select(getProjectOverviewSystems);

    this.groups$ = combineLatest([overview$, systems$, this.refresh$, this.filter$]).pipe(
      map(([items, systems, _refresh, filter]) => {
        const all = Array.isArray(items) ? items : [];
        const bySystemData = new Map<string, ProjectOverview[]>();
        all
          .filter((it) => this.itemMatchesFilter(it, filter))
          .forEach((item) => {
            const sys = (item?.systemNumber || 'unknown').toString();
            const list = bySystemData.get(sys) || [];
            list.push(item);
            bySystemData.set(sys, list);
          });

        const systemsArray = (systems as ProjectOverviewSystemState[]) || [];

        const groups: OverviewGroup[] = systemsArray
          .filter((s) => {
            // drop systems with no checksheets
            if ((s.checkSheetCount || 0) === 0) return false;

            // if a filter is active (not 'All') and there are no items for this system after filtering,
            // hide the system as requested
            const systemKey = (s.systemNumber || 'unknown').toString();
            const itemsForSystem = bySystemData.get(systemKey) || [];
            if (filter !== OverviewFilter.All && itemsForSystem.length === 0) return false;

            return true;
          })
          .sort((a, b) => a.systemNumber.localeCompare(b.systemNumber))
          .map((s) => {
            const systemKey = (s.systemNumber || 'unknown').toString();
            const rawItemsForSystem = bySystemData.get(systemKey) || [];

            const itemsForSystem = rawItemsForSystem.slice().sort((a, b) => {
              // 1) Primary: group by commissioningPackageNumber
              const aCPNum = (a.commissioningPackageNumber || '').toString();
              const bCPNum = (b.commissioningPackageNumber || '').toString();
              const cpGroupCompare = aCPNum.localeCompare(bCPNum, undefined, { numeric: true, sensitivity: 'base' });
              if (cpGroupCompare !== 0) {
                return cpGroupCompare;
              }

              // 2) Within a commissioning package, CP checksheets first
              const aIsCP = this.isCpCheckSheet(a);
              const bIsCP = this.isCpCheckSheet(b);
              if (aIsCP && !bIsCP) {
                return -1;
              }
              if (!aIsCP && bIsCP) {
                return 1;
              }

              // 3) For non-CP checksheets, sort by mcPackageNumber
              if (!aIsCP && !bIsCP) {
                const aMC = (a.mcPackageNumber || '').toString();
                const bMC = (b.mcPackageNumber || '').toString();
                const mcCompare = aMC.localeCompare(bMC, undefined, { numeric: true, sensitivity: 'base' });
                if (mcCompare !== 0) {
                  return mcCompare;
                }
              }

              // 4) Fallback: checkSheetNumber for deterministic ordering
              const aCS = (a.checkSheetNumber || '').toString();
              const bCS = (b.checkSheetNumber || '').toString();
              return aCS.localeCompare(bCS, undefined, { numeric: true, sensitivity: 'base' });
            });

            const punch = {
              aEntered: this.sum(itemsForSystem, 'apunchEnteredCount'),
              aAccepted: this.sum(itemsForSystem, 'apunchAcceptedCount'),
              aCleared: this.sum(itemsForSystem, 'apunchlearedCount'),
              bEntered: this.sum(itemsForSystem, 'bpunchEnteredCount'),
              bAccepted: this.sum(itemsForSystem, 'bpunchAcceptedCount'),
              bCleared: this.sum(itemsForSystem, 'bpunchClearedCount'),
              total: this.sum(itemsForSystem, 'punchTotalCount'),
            };

            const mccIssued = this.uniqueCount(itemsForSystem, 'mccCertificateNumber');
            const rfccIssued = this.uniqueCount(itemsForSystem, 'rfccCertificateNumber');
            const rfocIssued = this.uniqueCount(itemsForSystem, 'rfocCertificateNumber');

            const mccSigned = this.uniqueCountWhere(itemsForSystem, 'mccCertificateNumber', (it) => !!it.mccSigned);
            const rfccSigned = this.uniqueCountWhere(itemsForSystem, 'rfccCertificateNumber', (it) => !!it.rfccSigned);
            const rfocSigned = this.uniqueCountWhere(itemsForSystem, 'rfocCertificateNumber', (it) => !!it.rfocSigned);

            const certificates = {
              mcc: { issued: mccIssued, signed: mccSigned },
              rfcc: { issued: rfccIssued, signed: rfccSigned },
              rfoc: { issued: rfocIssued, signed: rfocSigned },
              none: {
                count: itemsForSystem.filter((it) => !it.mccCertificateNumber && !it.rfccCertificateNumber && !it.rfocCertificateNumber)
                  .length,
                total: itemsForSystem.length,
              },
            };

            const checksheetsSigned = {
              mcc: itemsForSystem.filter((it) => !!it.mccSigned).length,
              rfcc: itemsForSystem.filter((it) => !!it.rfccSigned).length,
              rfoc: itemsForSystem.filter((it) => !!it.rfocSigned).length,
            };

            return {
              system: systemKey,
              items: itemsForSystem,
              expanded: this.expandedMap.get(systemKey) ?? false,
              status: s.status,
              errorMessage: s.errorMessage,
              punch,
              certificates,
              checksheetsSigned,
            } as OverviewGroup;
          });

        return groups;
      }),
    );

    this.groups$.pipe(takeUntil(this.destroy$)).subscribe((g) => (this.latestGroups = g));

    // Sync filter control to filter BehaviorSubject
    this.filterControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.filter$.next(val ?? OverviewFilter.All);
      this.refresh$.next(Date.now());
    });
  }

  downloadSelection(): void {
    this.store
      .select(getProjectOverview)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        const excelData = items.map((csa) => ({
          CP: csa.commissioningPackageNumber ?? '',
          MCP: csa.mcPackageNumber ?? '',
          Tag: csa.tagNumber ?? '',
          Checksheet: csa.checkSheetNumber ?? '',
          Company: csa.companyNumber ?? '',
          Phase: csa.phaseNumber ?? '',
          Site: csa.siteNumber ?? '',
          Status: csa.status ?? '',
          SignedBy: csa.signedByUserName ?? '',
          SignedDate: csa.signedByTimestamp ?? '',
          VerifiedBy: csa.verifiedByUserName ?? '',
          VerifiedDate: csa.verifiedByTimestamp ?? '',
        }));
        this.excelService.generateExcel(excelData, `system-scope-${Date.now()}`);
      });
  }

  toggleGroup(system: string): void {
    this.expandedMap.set(system, !this.expandedMap.get(system));
    this.refresh$.next(Date.now());
  }

  trackBySystem(_i: number, g: OverviewGroup): string {
    return g.system;
  }
  trackByItem(_i: number, item: ProjectOverview): number {
    return item?.id ?? _i;
  }

  findTag(): void {
    const q = (this.search.value || '').toString().trim().toLowerCase();
    this.highlightedId = null;
    this.highlightedIds.clear();
    if (!q) {
      this.refresh$.next(Date.now());
      return;
    }
    let firstMatchId: number | null = null;
    for (const g of this.latestGroups) {
      let groupHasMatch = false;
      for (const it of g.items) {
        const tag = (it?.tagNumber || '').toString().toLowerCase();
        if (tag.includes(q)) {
          const id = it.id ?? -1;
          if (id !== -1) {
            this.highlightedIds.add(id);
            groupHasMatch = true;
            if (firstMatchId === null) firstMatchId = id;
          }
        }
      }
      if (groupHasMatch) {
        this.expandedMap.set(g.system, true);
      }
    }
    this.refresh$.next(Date.now());
    if (firstMatchId !== null) {
      setTimeout(
        () => document.getElementById(`overview-item-${firstMatchId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        50,
      );
    }
  }

  clearHighlight(): void {
    this.highlightedId = null;
    this.highlightedIds.clear();
    this.search.setValue('');
    this.refresh$.next(Date.now());
  }

  isHighlighted(item: ProjectOverview): boolean {
    const id = item?.id;
    return typeof id === 'number' && this.highlightedIds.has(id);
  }

  // Create a safe CSS class based on the checksheet number (e.g. 'CP-01' -> 'cs-cp-01')
  getChecksheetClass(item: ProjectOverview): string {
    const raw = (item.checkSheetNumber || '').toString().toLowerCase();
    const safe = raw.replace(/[^a-z0-9]+/g, '-');
    return safe ? `cs-${safe}` : '';
  }

  /**
   * Compute an inline background-color for a row based on:
   * - Family: CP (CPCL/CPCR) vs MC (MCCL/MCCR)
   * - Within MC: MCCL (lighter cyan) vs MCCR (deeper indigo)
   * - Discipline letter (E/H/I/L/R/S/T/Z/A/C/...) tweaks the alpha
   * - Trailing number (01, 02, 03, ...) slightly darkens the color
   */
  getChecksheetTintStyle(item: ProjectOverview): { [key: string]: string } | null {
    const id = (item.checkSheetNumber || '').toString().toUpperCase().trim();
    if (!id) return null;

    // Return cached style if we’ve already computed it for this checksheet type
    const cached = this.checksheetTintCache.get(id);
    if (cached) {
      return cached;
    }

    const family = id.startsWith('CPCL-') || id.startsWith('CPCR-') ? 'CP' : id.startsWith('MCCL-') || id.startsWith('MCCR-') ? 'MC' : null;
    if (!family) return null; // leave non CP/MC rows un-tinted here

    const isCPCL = id.startsWith('CPCL-');
    const isCPCR = id.startsWith('CPCR-');
    const isMCCL = id.startsWith('MCCL-');
    const isMCCR = id.startsWith('MCCR-');

    // Discipline is the segment after family prefix, e.g. CPCL-E-01 -> 'E'
    const parts = id.split('-');
    const discipline = parts.length > 1 ? parts[1] : '';

    // Extract trailing number from the last segment, e.g. MCCL-I-02YND -> segment '02YND' -> num 2
    let num = 0;
    const lastSegment = parts[parts.length - 1] || '';
    const numPrefixMatch = lastSegment.match(/^(\d{1,2})/);
    if (numPrefixMatch) {
      num = parseInt(numPrefixMatch[1], 10) || 0;
    }

    // Base RGBA for each family/cluster
    let r = 255,
      g = 255,
      b = 255,
      a = 0.0;

    if (family === 'CP') {
      // CP: yellow base
      if (isCPCL) {
        // CPCL: softer yellow
        r = 255;
        g = 235;
        b = 59;
        a = 0.16;
      } else if (isCPCR) {
        // CPCR: stronger yellow/orange
        r = 255;
        g = 215;
        b = 64;
        a = 0.2;
      }
      // Discipline tweaks the tint (RGB) slightly, not the shade
      switch (discipline) {
        case 'E': // slightly warmer yellow
          g += 8;
          b -= 6;
          break;
        case 'R': // push a bit more towards orange
          g -= 14;
          b -= 10;
          break;
        case 'H':
        case 'S': // cooler yellow
          g += 8;
          b += 8;
          break;
        case 'L': // more muted, slightly greener
          g -= 12;
          b += 10;
          break;
        case 'Z': // more muted, slightly redder
          g -= 16;
          b += 4;
          break;
        case 'A': // softer neutral yellow
          g -= 10;
          b += 14;
          break;
        case 'C': // distinct soft amber
          g -= 18;
          b += 12;
          break;
        case 'M': // mechanical: slightly darker, orange-leaning
          g -= 20;
          b -= 4;
          break;
        default:
          break;
      }
    } else if (family === 'MC') {
      if (isMCCL) {
        // MCCL: lighter cyan-blue
        r = 33;
        g = 150;
        b = 243;
        a = 0.16;
      } else if (isMCCR) {
        // MCCR: deeper indigo-blue
        r = 63;
        g = 81;
        b = 181;
        a = 0.16;
      }
      // Discipline tweaks the tint (RGB) within the blue family
      switch (discipline) {
        case 'E': // more cyan for electrical
          g += 18;
          b += 8;
          break;
        case 'R': // slightly more purple
          r += 14;
          b -= 6;
          break;
        case 'H':
        case 'S': // a touch more teal
          g += 14;
          b += 2;
          break;
        case 'I': // a bit more vivid blue
          b += 14;
          break;
        case 'L': // desaturated, leaning towards grey-blue
          g -= 16;
          b -= 8;
          break;
        case 'Z': // desaturated, slightly warmer blue
          g -= 10;
          b -= 12;
          r += 6;
          break;
        case 'A': // admin/aux: softer, greener blue
          g += 10;
          b -= 4;
          break;
        case 'M': // mechanical: darker, steel blue
          r -= 10;
          g += 18;
          b -= 10;
          break;
        default:
          break;
      }
    }

    // Clamp RGB after tint adjustments
    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));

    // Darken based on trailing number primarily by increasing alpha (less transparent)
    // so that 01 is lighter and higher numbers are more opaque.
    // Use a simple proportional factor: extra alpha = 0.02 * checksheetNumber (capped).
    // If we don't have a numeric suffix (num === 0), treat it as an "extra bright" variant
    // by nudging alpha down slightly instead of up.
    if (num > 0) {
      const extra = Math.min(num * 0.02, 0.3); // 01 -> +0.02, 05 -> +0.10, 10 -> +0.20, cap at +0.30
      a += extra;
    } else {
      // Extra bright for non-numbered checksheets
      a *= 0.85;
    }

    // Clamp alpha after all adjustments
    if (a < 0.03) a = 0.03;
    if (a > 0.5) a = 0.5;

    const color = `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
    const style = { 'background-color': color };
    this.checksheetTintCache.set(id, style);
    return style;
  }

  setFilter(filter: OverviewFilter): void {
    this.filterControl.setValue(filter);
  }

  setFilterFromString(value: string | null | undefined): void {
    if (!value) {
      this.setFilter(OverviewFilter.All);
      return;
    }
    // value will already be one of the OverviewFilter enum string values
    this.setFilter(value as OverviewFilter);
  }

  private itemMatchesFilter(it: ProjectOverview, f: OverviewFilter): boolean {
    const isSigned = !!it.signedByTimestamp; // generic signed flag
    const isVerified = !!it.verifiedByTimestamp;
    const signedNotVerified = isSigned && !isVerified;
    const progressPct = this.itemProgressPercent(it);
    const signedAndIncomplete = isSigned && progressPct < 100;

    // Punch status vs open punches consistency
    const status = (it.status || '').toString().toUpperCase();
    const openA = (it.apunchEnteredCount ?? 0) - (it.apunchlearedCount ?? 0);
    const openB = (it.bpunchEnteredCount ?? 0) - (it.bpunchClearedCount ?? 0);
    const hasOpenA = openA > 0;
    const hasOpenB = openB > 0;
    const isPA = status === 'PA';
    const isPB = status === 'PB';

    // Error if there are open A punches but status is not PA
    const punchStatusMismatchA = hasOpenA && !isPA;
    // Error if there are open B punches but status is not PB
    const punchStatusMismatchB = hasOpenB && !isPB;

    // Certificate membership: item is in any certificate
    const inCertificate = !!it.mccCertificateNumber || !!it.rfccCertificateNumber || !!it.rfocCertificateNumber;
    // Error if item is in a certificate but not both signed and verified
    const certNotSignedAndVerified = inCertificate && !(isSigned && isVerified);

    switch (f) {
      case OverviewFilter.SignedChecksheets:
        return isSigned;
      case OverviewFilter.VerifiedChecksheets:
        return isVerified;
      case OverviewFilter.SignedNotVerified:
        return signedNotVerified;
      case OverviewFilter.Errors:
        return (
          signedAndIncomplete || this.hasRfocWithoutPrereqs(it) || punchStatusMismatchA || punchStatusMismatchB || certNotSignedAndVerified
        );
      case OverviewFilter.SignedMCC:
        return !!it.mccSigned;
      case OverviewFilter.SignedRFCC:
        return !!it.rfccSigned;
      case OverviewFilter.SignedRFOC:
        return !!it.rfocSigned;
      case OverviewFilter.OpenAPunch:
        return (it.apunchEnteredCount ?? 0) - (it.apunchlearedCount ?? 0) > 0;
      case OverviewFilter.OpenBPunch:
        return (it.bpunchEnteredCount ?? 0) - (it.bpunchClearedCount ?? 0) > 0;
      case OverviewFilter.RFOCSignedWithOpenPunch:
        return (
          !!it.rfocSigned &&
          ((it.apunchEnteredCount ?? 0) - (it.apunchlearedCount ?? 0) > 0 ||
            (it.bpunchEnteredCount ?? 0) - (it.bpunchClearedCount ?? 0) > 0)
        );
      case OverviewFilter.All:
      default:
        return true;
    }
  }

  private sum(list: ProjectOverview[], key: keyof ProjectOverview): number {
    return list.reduce((acc, it) => {
      const v = it?.[key];
      return acc + (typeof v === 'number' ? v : 0);
    }, 0);
  }

  private uniqueCount(list: ProjectOverview[], key: keyof ProjectOverview): number {
    const s = new Set<string>();
    for (const it of list) {
      const v = it?.[key] as unknown as string | null | undefined;
      const val = typeof v === 'string' && v.trim().length ? v.trim() : undefined;
      if (val) s.add(val);
    }
    return s.size;
  }

  private uniqueCountWhere(list: ProjectOverview[], key: keyof ProjectOverview, predicate: (it: ProjectOverview) => boolean): number {
    const s = new Set<string>();
    for (const it of list) {
      if (!predicate(it)) continue;
      const v = it?.[key] as unknown as string | null | undefined;
      const val = typeof v === 'string' && v.trim().length ? v.trim() : undefined;
      if (val) s.add(val);
    }
    return s.size;
  }

  getGroupCount(g: OverviewGroup): number {
    return g.items.length;
  }
  getGroupProgress(g: OverviewGroup): number {
    const list = g?.items || [];
    if (!list.length) return 0;
    const percents = list.map((it) => this.itemProgressPercent(it));
    const sum = percents.reduce((a, b) => a + b, 0);
    return Math.round(sum / list.length);
  }

  itemProgressPercent(item: ProjectOverview): number {
    const v = typeof item?.progress === 'number' ? item.progress : 0;
    // If value appears to be a fraction (<=1) convert to percentage, else assume already percentage 0..100
    return v <= 1 ? v * 100 : v;
  }

  hasPunchMismatch(g: OverviewGroup): boolean {
    const p = g.punch;
    const subSum = p.aEntered + p.aAccepted + p.aCleared + p.bEntered + p.bAccepted + p.bCleared;
    const total = typeof p.total === 'number' ? p.total : 0;
    return subSum !== total;
  }

  hasRfocOpenAPunch(g: OverviewGroup): boolean {
    if (!g?.certificates?.rfoc?.signed) return false;
    const openA = g.punch.aEntered - g.punch.aCleared;
    return openA > 0;
  }

  hasRfocOpenBPunch(g: OverviewGroup): boolean {
    if (!g?.certificates?.rfoc?.signed) return false;
    const openB = g.punch.bEntered - g.punch.bCleared;
    return openB > 0;
  }

  hasVerifiedNotSigned(item: ProjectOverview): boolean {
    return !!item.verifiedByTimestamp && !item.signedByTimestamp;
  }

  hasSignedProgressIncomplete(item: ProjectOverview): boolean {
    return !!item.signedByTimestamp && this.itemProgressPercent(item) < 100;
  }

  /** True when there are open A-punches but status is not PA */
  hasPunchStatusMismatchA(item: ProjectOverview): boolean {
    const status = (item.status || '').toString().toUpperCase();
    const openA = (item.apunchEnteredCount ?? 0) - (item.apunchlearedCount ?? 0);
    const hasOpenA = openA > 0;
    const isPA = status === 'PA';
    return hasOpenA && !isPA;
  }

  /** True when there are open B-punches but status is not PB */
  hasPunchStatusMismatchB(item: ProjectOverview): boolean {
    const status = (item.status || '').toString().toUpperCase();
    const openB = (item.bpunchEnteredCount ?? 0) - (item.bpunchClearedCount ?? 0);
    const hasOpenB = openB > 0;
    const isPB = status === 'PB';
    return hasOpenB && !isPB;
  }

  /** True when the item belongs to any certificate but is not both signed and verified */
  hasCertificateNotSignedAndVerified(item: ProjectOverview): boolean {
    const inCertificate = !!item.mccCertificateNumber || !!item.rfccCertificateNumber || !!item.rfocCertificateNumber;
    const isSigned = !!item.signedByTimestamp;
    const isVerified = !!item.verifiedByTimestamp;
    return inCertificate && !(isSigned && isVerified);
  }

  hasRfocWithoutPrereqs(item: ProjectOverview): boolean {
    const hasRfoc = !!item.rfocCertificateNumber || !!item.rfocSigned;
    const lacksPrereqs = !item.rfccCertificateNumber && !item.mccCertificateNumber;
    const notCP = !(item.checkSheetNumber || '').toUpperCase().includes('CP');
    return hasRfoc && lacksPrereqs && notCP;
  }

  private getRfocSignedDays(items: ProjectOverview[]): number | null {
    const dates: number[] = [];
    for (const it of items) {
      if (!it.rfocSigned) continue;
      const d = (it as any).rfocSignedTimestamp ?? it.signedByTimestamp;
      const t = d ? new Date(d).getTime() : NaN;
      if (!isNaN(t)) dates.push(t);
    }
    if (!dates.length) return null;
    const latest = Math.max(...dates);
    const ms = Date.now() - latest;
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  }

  getSystemAlertClass(g: OverviewGroup): string | null {
    const hasRfoc = g.certificates?.rfoc?.signed > 0 || g.certificates?.rfoc?.issued > 0;
    const days = this.getRfocSignedDays(g.items);
    const hasOpenPunch = this.hasRfocOpenAPunch(g) || this.hasRfocOpenBPunch(g);
    const allCerts = g.certificates?.none?.count === 0;

    // Success: RFOC present, no open punches, and all checksheets are in a certificate
    if (hasRfoc && !hasOpenPunch && allCerts) return 'alert-success';

    if (hasRfoc && typeof days === 'number' && hasOpenPunch) {
      if (days > 30) return 'alert-danger';
      if (days >= 15) return 'alert-warn';
      if (days >= 0) return 'alert-yellow';
    }
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isCpCheckSheet(item: ProjectOverview): boolean {
    const cs = (item.checkSheetNumber || '').toString().toUpperCase();
    return cs.startsWith('CP');
  }
}
